import { useState, useEffect, useRef } from 'react';
import { SignalSlashIcon, WifiIcon } from '@heroicons/react/24/solid';
import useOnlineStatus from '../../hooks/useOnlineStatus';
import { OptimizedImage } from '../../utils/imageManager';
import ActionButton from './ActionButton';

const OfflineOverlay = () => {
  const isOnline = useOnlineStatus();
  const [showOverlay, setShowOverlay] = useState(!navigator.onLine);
  const [showBackOnline, setShowBackOnline] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);
  const wasOffline = useRef(!navigator.onLine);

  useEffect(() => {
    if (!isOnline) {
      setShowOverlay(true);
      setShowBackOnline(false);
      wasOffline.current = true;
    } else if (wasOffline.current) {
      setShowOverlay(false);
      setRetryFailed(false);
      setShowBackOnline(true);
      setTimeout(() => setShowBackOnline(false), 3000);
      wasOffline.current = false;
    }
  }, [isOnline]);

  const handleRetry = async () => {
    setIsRetrying(true);
    setRetryFailed(false);
    if (!navigator.onLine) {
      setRetryFailed(true);
      setIsRetrying(false);
      return;
    }
    try {
      await fetch(`/?_=${Date.now()}`, { method: 'HEAD', cache: 'no-store', signal: AbortSignal.timeout(5000) });
      setShowOverlay(false);
      setShowBackOnline(true);
      setTimeout(() => setShowBackOnline(false), 3000);
      wasOffline.current = false;
    } catch {
      setRetryFailed(true);
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <>
      {showOverlay && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 backdrop-blur-md bg-background-primary/80">
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none" />

          <div
            className="relative w-full max-w-md rounded-2xl shadow-2xl border border-border border-t-4 flex flex-col overflow-hidden"
            style={{
              background: 'var(--color-background-secondary)',
              borderTopColor: 'var(--color-secondary)',
            }}
          >
            {/* Header */}
            <div className="relative p-4 flex items-center overflow-hidden flex-shrink-0">
              <div
                className="relative z-10 w-8 h-8 rounded-lg flex items-center justify-center mr-3 backdrop-blur-sm border border-white/20"
                style={{
                  background: 'var(--color-glassy-purple)',
                  boxShadow: '0 4px 12px color-mix(in srgb, var(--color-glassy-purple) 30%, transparent)',
                }}
              >
                <SignalSlashIcon className="h-4 w-4 text-white drop-shadow-sm" />
              </div>
              <h3 className="relative z-10 text-lg font-bold text-content-primary">No Internet Connection</h3>
            </div>

            {/* Content */}
            <div className="flex-1 p-6 text-center">
              <div className="relative inline-block mb-4">
                <div
                  className="w-28 h-28 rounded-full backdrop-blur-sm border border-border flex items-center justify-center overflow-hidden"
                  style={{
                    background: 'var(--color-background-primary)',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30 rounded-full" />
                  <OptimizedImage
                    imageKey="ACTION_NO_INTERNET"
                    className="relative z-10 w-20 h-20 object-contain"
                    fallback={<span className="text-4xl">📡</span>}
                  />
                </div>
              </div>
              <p className="text-base text-content-secondary leading-relaxed">
                {retryFailed
                  ? 'Still no connection. Please check your internet and try again.'
                  : 'This app requires an internet connection to sync your progress.'}
              </p>
            </div>

            {/* Footer */}
            <div
              className="px-6 py-4 flex justify-center border-t border-border flex-shrink-0"
              style={{ background: 'var(--color-background-primary)' }}
            >
              <ActionButton
                variant="glassy"
                color="purple"
                onClick={handleRetry}
                loading={isRetrying}
                className="px-6 py-2 text-white"
              >
                Try again
              </ActionButton>
            </div>
          </div>
        </div>
      )}

      {/* Back online banner */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white shadow-lg transition-all duration-300 ease-in-out backdrop-blur-md border-t border-white/20 ${
          showBackOnline ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
        style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.92), rgba(5, 150, 105, 0.85))' }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-white/25 to-transparent pointer-events-none" />
        <WifiIcon className="relative h-4 w-4 shrink-0 drop-shadow-sm" />
        <span className="relative drop-shadow-sm">Back online.</span>
      </div>
    </>
  );
};

export default OfflineOverlay;
